import React from "react";
import { useTranslation } from "react-i18next";

const getBarColor = (value) => {
  if (value >= 75) return "#00AFB9"; // high
  if (value >= 50) return "#FFD166"; // mid
  if (value > 0) return "#EF476F";   // low
  return "#f07167";                  // 0
};

function ComplianceCard({ standard, viewMode = "grid" }) {
  const { t } = useTranslation();

  const statusLabel = (s) => {
    if (s === "Compliant") return t("complianceOverview.compliant");
    if (s === "Partial") return t("complianceOverview.partial");
    if (s === "Non-Compliant") return t("complianceOverview.nonCompliant");
    return s;
  };

  const categoryLabel = (c) => (c === "General" ? t("complianceOverview.general") : c);

  const complianceLevel =
    standard?.audit_score != null
      ? Math.round(Number(standard.audit_score))
      : null;
  const status =
    standard?.status ||
    (complianceLevel == null
      ? "Partial"
      : complianceLevel >= 80
      ? "Compliant"
      : complianceLevel >= 50
      ? "Partial"
      : "Non-Compliant");

  const category = standard?.category || "General";

      //console.log(standard)
  const statusStyles = {
    Compliant: {
      chipBg: "bg-green-50",
      chipText: "text-green-700",
      chipBorder: "border-green-200",
      dot: "bg-green-600",
    },
    Partial: {
      chipBg: "bg-amber-50",
      chipText: "text-amber-700",
      chipBorder: "border-amber-200",
      dot: "bg-amber-500",
    },
    "Non-Compliant": {
      chipBg: "bg-rose-50",
      chipText: "text-rose-700",
      chipBorder: "border-rose-200",
      dot: "bg-rose-500",
    },
  };

  const style = statusStyles[status] || statusStyles.Partial;

  const barColor = getBarColor(complianceLevel ?? 0);

  // keep list/grid support (optional future tweaks)
  const isList = viewMode === "list";

  const formatAuditDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    if (isNaN(d)) return "-";

    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };


return (
  <div className="bg-white shadow-sm border border-gray-200 rounded-xl p-4 transition hover:shadow-md flex flex-col h-full">
    {/* Title + status chip */}
    <div className={`flex items-start justify-between gap-3 ${isList ? "gap-4" : ""}`}>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-[#0E0E0E] truncate">
          {standard?.name || "Untitled"}
        </h3>
        <p className="mt-1 text-xs text-gray-600 leading-snug line-clamp-2">
          {standard?.description || t("complianceOverview.noDescription")}
        </p>
      </div>

      <span
        className={`shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${style.chipBg} ${style.chipText} ${style.chipBorder}`}
      >
        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
        {statusLabel(status)}
      </span>
    </div>

    {/* Compliance Score */}
    <div className="mt-4">
      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
        <span>{t("complianceOverview.complianceScore")}</span>
        <span className="text-sm font-semibold text-gray-800">
          {complianceLevel !== null ? `${complianceLevel}%` : t("complianceOverview.noData")}
        </span>
      </div>

      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${complianceLevel || 0}%`,
            backgroundColor: barColor,
          }}
        />
      </div>
    </div>

    {/* Last Audit */}
    <div className="mt-4 text-xs text-gray-700">
      <p className="text-gray-500">{t("complianceOverview.lastAudit")}</p>
      <p className="font-semibold">{formatAuditDate(standard?.audit_start_date)}</p>
    </div>

    {/* ✅ Bottom row pinned + aligned */}
    <div className="mt-auto pt-4 flex items-center justify-between">
      <span className="inline-block px-3 py-1 text-[11px] bg-gray-100 border border-gray-300 rounded-full text-gray-700">
        {categoryLabel(category)}
      </span>

      {standard?.company_name && (
        <span className="text-[11px] text-gray-400">
          {standard.company_name}
        </span>
      )}
    </div>
  </div>
);




}

export default ComplianceCard;
